/**
 * Overlay behaviour shared by Modal, Drawer, the command palette and the help popover.
 *
 * Written once because getting it wrong is invisible until a keyboard user meets it: an
 * overlay that does not trap focus lets Tab walk into the page behind, and an overlay that
 * does not restore focus on close drops the operator back at the top of the document.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Every overlay is a stack; only the top one answers Escape. */
const stack: symbol[] = [];

export function useEscapeKey(active: boolean, onEscape: () => void): void {
  const handler = useRef(onEscape);
  handler.current = onEscape;

  useEffect(() => {
    if (!active) return;
    const token = Symbol('overlay');
    stack.push(token);

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      if (stack[stack.length - 1] !== token) return;
      event.stopPropagation();
      handler.current();
    };

    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const index = stack.indexOf(token);
      if (index !== -1) stack.splice(index, 1);
    };
  }, [active]);
}

/**
 * Locks the page behind the overlay. Compensates for the scrollbar so the content does
 * not shift sideways when a dialog opens — a 15px jolt reads as a bug even when nobody
 * can say what happened.
 */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = 'hidden';
    if (gap > 0) body.style.paddingRight = `${gap}px`;
    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, [active]);
}

/**
 * Traps Tab inside `container` while active, moves focus in on open, and puts it back
 * where it came from on close.
 */
export function useFocusTrap(active: boolean, container: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!active) return;
    const node = container.current;
    if (!node) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );

    /*
     * Where focus lands on open, in order:
     *
     *   1. anything that asked for it (`data-autofocus`)
     *   2. the first real input — a dialog you are meant to fill in should be ready to type
     *   3. the first control that is NOT the dismiss button
     *   4. the container, so the dialog's own text is announced
     *
     * The close button is explicitly last. Landing on it put a focus ring around the header
     * ✕ of four of the five dialogs in the review set — which is why the same one component
     * read as "a boxed ×" on those and "a bare ×" on the one that autofocused its date
     * field. It is also the wrong first suggestion: the first thing a dialog offers should
     * never be the way out of it.
     */
    const notClose = (el: HTMLElement): boolean => el.dataset.ndClose !== 'true';
    const items = focusables();
    const initial =
      node.querySelector<HTMLElement>('[data-autofocus]') ??
      items.find((el) => notClose(el) && /^(input|textarea|select)$/i.test(el.tagName)) ??
      items.find(notClose) ??
      items[0] ??
      node;
    // preventScroll: a dialog that scrolls the page behind it before covering it produces a
    // visible lurch through the gap at the edge of the scrim.
    window.setTimeout(() => initial.focus({ preventScroll: true }), 0);

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;
      const current = document.activeElement;
      if (event.shiftKey && (current === first || current === node)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    /*
     * The Tab handler above is attached to the overlay, so it only fires while focus is
     * already inside. Focus can arrive outside it anyway — a click on the page behind, a
     * browser find bar handing focus back, a screen reader's own navigation — and from
     * there Tab walks the page underneath a dialog that claims aria-modal. This pulls it
     * back. It is the cheap half of a focus trap and the half that is usually missing.
     */
    const onFocusIn = (event: FocusEvent): void => {
      const target = event.target as Node | null;
      if (!target || node.contains(target)) return;
      // A nested overlay (a confirm dialog opened from inside a drawer) is legitimately
      // outside this node. Only reclaim focus if nothing deeper has taken over.
      const el = target instanceof Element ? target : target.parentElement;
      if (el?.closest('[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]')) return;
      const items = focusables();
      (items[0] ?? node).focus({ preventScroll: true });
    };

    node.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      node.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      /*
       * FOCUS RETURN, and the scroll position that comes with it.
       *
       * Round one made focus come back to the element that opened the overlay. What it did
       * not do is stop the browser scrolling that element into the middle of the viewport
       * on the way — so closing a drawer opened from row 34 of a table re-centred the list
       * and the operator lost their place in exactly the moment the drawer exists to
       * prevent. `preventScroll` then `block: 'nearest'` restores focus without moving
       * anything that is already visible.
       */
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
        previouslyFocused.scrollIntoView({ block: 'nearest' });
      }
    };
  }, [active, container]);
}

/** Fires when a click or focus lands outside `container`. Used by popovers, not dialogs. */
export function useDismissOnOutside(
  active: boolean,
  container: RefObject<HTMLElement | null>,
  onDismiss: () => void,
): void {
  const handler = useRef(onDismiss);
  handler.current = onDismiss;

  useEffect(() => {
    if (!active) return;
    const onPointer = (event: MouseEvent): void => {
      const node = container.current;
      if (node && !node.contains(event.target as Node)) handler.current();
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [active, container]);
}

/** Stable id for aria-labelledby / aria-describedby wiring. */
let idCounter = 0;
export function useOverlayId(prefix: string): string {
  const ref = useRef<string>('');
  if (!ref.current) ref.current = `${prefix}-${++idCounter}`;
  return ref.current;
}

/** Small helper so overlays can close on scrim click without closing on content click. */
export function useScrimHandler(onDismiss: () => void): (event: ReactMouseEvent) => void {
  return useCallback(
    (event: ReactMouseEvent) => {
      if (event.target === event.currentTarget) onDismiss();
    },
    [onDismiss],
  );
}
